import React, { useState } from 'react';
import { Check, X, Bot, Plug, MessageSquare, Zap, Users, BarChart3, Shield, Headphones, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import Navbar from './Navbar';
import Footer from './Footer';
import { useAuth } from '../context/AuthContext';

// Stripe Payment Links
const STRIPE_LINKS = {
  lite: 'https://buy.stripe.com/9B69AU82x9JPfPe2w4',
  business: 'https://buy.stripe.com/aFaaEY6Yt5tz0Ukb2A',
  enterprise: 'https://buy.stripe.com/aFadRabeJ1dj7iIgmU'
};

const PricingPage = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const { user } = useAuth();

  const handleSubscribe = (plan) => {
    // Add email prefill if user is logged in
    let url = STRIPE_LINKS[plan];
    if (user?.email) {
      url += `?prefilled_email=${encodeURIComponent(user.email)}`;
    }
    window.open(url, '_blank');
  };

  const plans = [
    {
      id: 'lite',
      name: 'Lite',
      price: 29,
      conversations: 500,
      extraConversation: '$0.04',
      description: 'Para pequenos negócios começando',
      popular: false,
      features: {
        channels: ['Website chat widget', 'QR Codes'],
        automations: ['Chatbots ilimitados', 'Respostas automáticas', 'Fluxos visuais básicos'],
        features: ['Live chat básico', 'Gerenciamento de contatos'],
        integrations: ['Google Sheets', 'Webhooks'],
        support: 'Email'
      },
      limitations: ['Sem WhatsApp/Instagram/Facebook', 'Sem integração ChatGPT', 'Sem broadcasts']
    },
    {
      id: 'business',
      name: 'Business',
      price: 39,
      conversations: 1000,
      extraConversation: '$0.04',
      description: 'Para empresas que querem crescer',
      popular: true,
      features: {
        channels: ['WhatsApp Business API', 'Instagram DM & Stories', 'Facebook Messenger', 'Website chat widget', 'Click-to-WhatsApp Ads', 'QR Codes', 'TikTok'],
        automations: ['Chatbots ilimitados', 'Respostas automáticas', 'Integração ChatGPT/GPT-4', 'Fluxos visuais (Flow Builder)', 'Keywords e triggers', 'Broadcasts em massa'],
        features: ['Live chat em tempo real', 'Caixa de entrada compartilhada', 'Atribuição de agentes', 'Gerenciamento de contatos', 'Segmentação avançada'],
        integrations: ['Google Sheets', 'Zapier (5000+ apps)', 'Stripe (pagamentos)', 'Calendly', 'API REST completa', 'Webhooks'],
        support: 'Chat e Email'
      },
      limitations: []
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 400,
      conversations: 15000,
      extraConversation: 'Negociável',
      description: 'Para grandes operações',
      popular: false,
      features: {
        channels: ['Todos os canais do Business', 'Integrações personalizadas'],
        automations: ['Tudo do Business', '10 horas de criação de bots grátis'],
        features: ['Tudo do Business', 'Relatórios avançados', 'Onboarding personalizado'],
        integrations: ['Todas do Business', 'Integrações customizadas', 'API dedicada'],
        support: '24/7 prioritário + Gerente dedicado'
      },
      limitations: []
    }
  ];

  const allPlanFeatures = [
    { icon: Zap, title: 'Flow Builder Visual', description: 'Crie automações complexas sem código com nosso editor drag-and-drop' },
    { icon: Bot, title: '30+ Templates Prontos', description: 'Comece rapidamente com templates para diferentes indústrias' },
    { icon: MessageSquare, title: 'Multi-canal', description: 'WhatsApp, Instagram, Facebook e Website em um só lugar' },
    { icon: Users, title: 'CRM Integrado', description: 'Gerencie leads e contatos com segmentação inteligente' },
    { icon: BarChart3, title: 'Analytics', description: 'Métricas detalhadas de conversas e conversões' },
    { icon: Shield, title: 'Segurança', description: 'Dados criptografados e conformidade com LGPD' }
  ];

  const faqs = [
    {
      question: 'Como as conversas são contadas?',
      answer: 'Uma conversa é uma sessão de troca de mensagens entre um usuário e um bot. A conversa é fechada após 24 horas de inatividade. Mensagens dentro da mesma sessão de 24h contam como uma única conversa.'
    },
    {
      question: 'Posso testar antes de assinar?',
      answer: 'Sim! Oferecemos 7 dias de teste grátis com acesso a todas as funcionalidades. Não é necessário cartão de crédito para começar.'
    },
    {
      question: 'Qual a diferença entre Lite e Business?',
      answer: 'O plano Lite é ideal para quem quer começar com chat no site. O Business inclui WhatsApp, Instagram, Facebook, TikTok, integração com ChatGPT, broadcasts e muito mais.'
    },
    {
      question: 'Posso fazer upgrade ou downgrade?',
      answer: 'Sim! Você pode mudar de plano a qualquer momento. O valor será ajustado proporcionalmente.'
    },
    {
      question: 'Como funciona a integração com WhatsApp?',
      answer: 'Utilizamos a API oficial do WhatsApp Business. Você precisa ter uma conta no Meta Business Suite. Nossa equipe te ajuda com todo o processo de verificação e configuração.'
    },
    {
      question: 'Vocês oferecem suporte em português?',
      answer: 'Sim! Nosso suporte é 100% em português e espanhol, disponível por chat e email.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />

      <main className="pt-24 pb-16">
        {/* Hero Header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Zap className="w-4 h-4" />
            7 dias grátis em todos os planos
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Planos simples,<br />
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
              resultados extraordinários
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Automatize seu atendimento e multiplique suas vendas com chatbots inteligentes
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
          <div className="grid lg:grid-cols-3 gap-8">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-white rounded-3xl shadow-xl overflow-hidden ${
                  plan.popular ? 'border-2 border-blue-500 lg:scale-105' : 'border border-gray-200'
                }`}
              >
                {plan.popular && (
                  <>
                    <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 to-cyan-500" />
                    <div className="absolute -top-px left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm font-semibold px-6 py-1.5 rounded-b-xl">
                        Mais Popular
                      </span>
                    </div>
                  </>
                )}

                <div className="p-8 pt-10">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-500 text-sm">{plan.description}</p>
                    <div className="mt-4">
                      <span className="text-5xl font-bold text-gray-900">${plan.price}</span>
                      <span className="text-gray-500 text-lg">/mês</span>
                    </div>
                    <p className="text-sm text-gray-400 mt-2">
                      {plan.conversations.toLocaleString()} conversas incluídas
                    </p>
                    <p className="text-xs text-gray-400">
                      + {plan.extraConversation}/conversa adicional
                    </p>
                  </div>

                  <Button
                    onClick={() => handleSubscribe(plan.id)}
                    className={`w-full py-6 text-lg font-semibold rounded-xl mb-6 ${
                      plan.popular
                        ? 'bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600'
                        : plan.id === 'enterprise'
                        ? 'bg-slate-900 hover:bg-slate-800'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    {plan.id === 'enterprise' ? 'Falar com vendas' : 'Começar teste grátis'}
                  </Button>

                  {/* Features */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Canais</h4>
                      <ul className="space-y-1.5">
                        {plan.features.channels.map((feature) => (
                          <li key={feature} className="flex items-center gap-2 text-sm text-gray-700">
                            <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Automações</h4>
                      <ul className="space-y-1.5">
                        {plan.features.automations.map((feature) => (
                          <li key={feature} className="flex items-center gap-2 text-sm text-gray-700">
                            <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Suporte</h4>
                      <p className="text-sm text-gray-700 flex items-center gap-2">
                        <Headphones className="h-4 w-4 text-blue-500" />
                        {plan.features.support}
                      </p>
                    </div>

                    {plan.limitations.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Limitações</h4>
                        <ul className="space-y-1.5">
                          {plan.limitations.map((limitation) => (
                            <li key={limitation} className="flex items-center gap-2 text-sm text-gray-400">
                              <X className="h-4 w-4 text-gray-300 flex-shrink-0" />
                              {limitation}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Features Grid */}
        <div className="bg-slate-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Incluso em todos os planos
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Ferramentas poderosas para automatizar seu atendimento e aumentar suas vendas
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {allPlanFeatures.map((feature) => (
                <div key={feature.title} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-500 text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Compare os planos</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-4 px-4 font-semibold text-gray-900">Recurso</th>
                    <th className="text-center py-4 px-4 font-semibold text-gray-900">Lite</th>
                    <th className="text-center py-4 px-4 font-semibold text-blue-600">Business</th>
                    <th className="text-center py-4 px-4 font-semibold text-gray-900">Enterprise</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-3 px-4 text-gray-700">Conversas/mês</td>
                    <td className="py-3 px-4 text-center">500</td>
                    <td className="py-3 px-4 text-center font-medium">1,000</td>
                    <td className="py-3 px-4 text-center">15,000</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="py-3 px-4 text-gray-700">WhatsApp</td>
                    <td className="py-3 px-4 text-center"><X className="h-5 w-5 text-gray-300 mx-auto" /></td>
                    <td className="py-3 px-4 text-center"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                    <td className="py-3 px-4 text-center"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">Instagram & Facebook</td>
                    <td className="py-3 px-4 text-center"><X className="h-5 w-5 text-gray-300 mx-auto" /></td>
                    <td className="py-3 px-4 text-center"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                    <td className="py-3 px-4 text-center"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="py-3 px-4 text-gray-700">Website Chat</td>
                    <td className="py-3 px-4 text-center"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                    <td className="py-3 px-4 text-center"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                    <td className="py-3 px-4 text-center"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">ChatGPT/GPT-4</td>
                    <td className="py-3 px-4 text-center"><X className="h-5 w-5 text-gray-300 mx-auto" /></td>
                    <td className="py-3 px-4 text-center"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                    <td className="py-3 px-4 text-center"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="py-3 px-4 text-gray-700">Broadcasts</td>
                    <td className="py-3 px-4 text-center"><X className="h-5 w-5 text-gray-300 mx-auto" /></td>
                    <td className="py-3 px-4 text-center"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                    <td className="py-3 px-4 text-center"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">Zapier</td>
                    <td className="py-3 px-4 text-center"><X className="h-5 w-5 text-gray-300 mx-auto" /></td>
                    <td className="py-3 px-4 text-center"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                    <td className="py-3 px-4 text-center"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="py-3 px-4 text-gray-700">Suporte dedicado</td>
                    <td className="py-3 px-4 text-center"><X className="h-5 w-5 text-gray-300 mx-auto" /></td>
                    <td className="py-3 px-4 text-center"><X className="h-5 w-5 text-gray-300 mx-auto" /></td>
                    <td className="py-3 px-4 text-center"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="bg-slate-50 py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Perguntas Frequentes</h2>
              <p className="text-gray-600">Tudo que você precisa saber sobre nossos planos</p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-gray-900">{faq.question}</span>
                    {expandedFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                  {expandedFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Pronto para automatizar seu negócio?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Comece hoje mesmo com 7 dias grátis. Sem cartão de crédito.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => handleSubscribe('business')}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 px-8 py-6 text-lg"
              >
                Começar com Business
              </Button>
              <Button
                onClick={() => handleSubscribe('lite')}
                variant="outline"
                size="lg"
                className="px-8 py-6 text-lg"
              >
                Começar com Lite
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PricingPage;
